import React from 'react';
import {
  FaUsers,
  FaProjectDiagram,
  FaMapMarkerAlt,
  FaFileInvoice,
  FaClipboardCheck,
  FaChartLine,
  FaCog,
  FaUserTie,
  FaBuilding,
  FaTruck,
  FaGraduationCap,
  FaUserClock,
  FaExclamationTriangle,
  FaCheckDouble,
  FaHourglassHalf,
  FaMoneyBillWave,
  FaCalendarCheck,
  FaFileAlt,
  FaShieldAlt,
  FaTools,
  FaReceipt,
} from 'react-icons/fa';
import {
  AdminDashboard,
  TeamCoordinatorDashboard,
  TechnicalAssociateDashboard,
  MISDashboard,
  SiteEngineerDashboard,
  ReportMakerDashboard,
  ReportValidatorDashboard,
  HRDashboard,
  BillingManagerDashboard,
  AccountsDashboard,
  ProjectManagerDashboard,
  WorkflowFunnel,
} from '../../../types/dashboard';
import {
  StatCard,
  SectionCard,
  MiniStat,
  StatusBadge,
  ProgressBar,
  DataTable,
  formatCurrency,
  formatDate,
  formatWorkflowStatus,
} from './ui';
import {
  DoughnutChart,
  BarChart,
  LineChart,
  WorkflowFunnelChart,
  ProgressRing,
  CHART_COLORS,
} from './charts';
import { getAttendanceLeaveTypeLabel } from '../../../libs/attendance';

const getRoleName = (role: { name?: string; 'role.name'?: string }) =>
  role.name || role['role.name'] || 'Unknown';

const toNumber = (value: number | string | undefined | null) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const formatTime = (value: string | null | undefined) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// Admin Dashboard Component (Role 1 & 2)
interface AdminDashboardViewProps {
  data: AdminDashboard;
}

export const AdminDashboardView: React.FC<AdminDashboardViewProps> = ({ data }) => {
  const { overview, users, projects, sites, billing, claims, attendance_today, training, pending_actions } = data;

  // Prepare workflow funnel data
  const workflowFunnelData = sites.workflow_funnel
    ? Object.entries(sites.workflow_funnel)
        .filter(([key]) => key !== 'validation_completed')
        .map(([key, value]) => ({
          label: formatWorkflowStatus(key),
          value: value as number,
        }))
    : [];

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total Users"
          value={overview.total_users}
          subtitle={`${overview.active_users} active`}
          icon={FaUsers}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Total Projects"
          value={overview.total_projects}
          subtitle={`${overview.active_projects} active`}
          icon={FaProjectDiagram}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          title="Sites This Month"
          value={overview.sites_this_month}
          icon={FaMapMarkerAlt}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />
        <StatCard
          title="Total Clients"
          value={overview.total_clients}
          subtitle={`${overview.active_clients} active`}
          icon={FaBuilding}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
        />
        <StatCard
          title="Total Vendors"
          value={overview.total_vendors}
          subtitle={`${overview.active_vendors} active`}
          icon={FaTruck}
          iconBgColor="bg-red-100"
          iconColor="text-red-600"
        />
      </div>

      {/* Pending Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Pending Leaves"
          value={pending_actions.pending_leaves}
          icon={FaCalendarCheck}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
        />
        <StatCard
          title="Pending Training"
          value={pending_actions.pending_training}
          icon={FaGraduationCap}
          iconBgColor="bg-indigo-100"
          iconColor="text-indigo-600"
        />
        <StatCard
          title="Pending Invoices"
          value={pending_actions.pending_invoices}
          icon={FaFileInvoice}
          iconBgColor="bg-red-100"
          iconColor="text-red-600"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
        {/* Users by Role */}
        <div className="xl:col-span-2">
          <SectionCard title="Users by Role" icon={FaUsers}>
            <div className="h-64">
              <BarChart
                labels={users.by_role.map((r) => getRoleName(r))}
                datasets={[
                  {
                    label: 'Users',
                    data: users.by_role.map((r) => toNumber(r.count)),
                    backgroundColor: CHART_COLORS.primary[1],
                  },
                ]}
                horizontal
                showLegend={false}
              />
            </div>
          </SectionCard>
        </div>

        {/* Projects by Status */}
        <SectionCard title="Projects by Status" icon={FaProjectDiagram}>
          <div className="h-64">
            <BarChart
              labels={projects.by_status.map((p) => formatWorkflowStatus(p.status))}
              datasets={[
                {
                  label: 'Projects',
                  data: projects.by_status.map((p) => p.count),
                  backgroundColor: CHART_COLORS.primary[1],
                },
              ]}
              showLegend={false}
            />
          </div>
        </SectionCard>

        {/* Today's Attendance */}
        <SectionCard title="Today's Attendance" icon={FaUserClock}>
          <div className="h-64">
            <DoughnutChart
              labels={['Present', 'Absent', 'On Leave']}
              data={[attendance_today.present, attendance_today.absent, attendance_today.on_leave]}
              colors={[CHART_COLORS.success[1], CHART_COLORS.danger[1], CHART_COLORS.warning[1]]}
              centerText={String(attendance_today.present)}
              centerSubtext="Present"
            />
          </div>
        </SectionCard>
      </div>

      {/* Sites Workflow Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Sites by Workflow Status" icon={FaCog}>
          <div className="h-80">
            <BarChart
              labels={sites.by_workflow_status.map((s) => formatWorkflowStatus(s.survey_workflow_status))}
              datasets={[
                {
                  label: 'Sites',
                  data: sites.by_workflow_status.map((s) => s.count),
                },
              ]}
              horizontal
              showLegend={false}
            />
          </div>
        </SectionCard>

        <SectionCard title="Workflow Funnel" icon={FaChartLine}>
          <WorkflowFunnelChart data={workflowFunnelData} />
        </SectionCard>
      </div>

      {/* Billing & Claims */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Billing Status" icon={FaFileInvoice}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <MiniStat
                label="Total Billed"
                value={formatCurrency(billing.total_billed_amount)}
                color="green"
              />
              <MiniStat
                label="Pending Invoices"
                value={billing.pending_invoices}
                color="yellow"
              />
            </div>
            <div className="h-48">
              <BarChart
                labels={billing.by_status.map((b) => formatWorkflowStatus(b.billing_status))}
                datasets={[
                  {
                    label: 'Count',
                    data: billing.by_status.map((b) => b.count),
                  },
                ]}
                showLegend={false}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Claims Overview" icon={FaMoneyBillWave}>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <MiniStat
                label="Project Claims"
                value={claims.project_claims.reduce((sum, c) => sum + Number(c.count ?? 0), 0)}
                color="blue"
              />
              <MiniStat
                label="General Claims"
                value={claims.general_claims.reduce((sum, c) => sum + Number(c.count ?? 0), 0)}
                color="green"
              />
              <MiniStat
                label="Advances"
                value={claims.advances.reduce((sum, c) => sum + Number(c.count ?? 0), 0)}
                color="yellow"
              />
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

// Project Manager Dashboard (Role 3)
interface ProjectManagerViewProps {
  data: ProjectManagerDashboard;
}

export const ProjectManagerView: React.FC<ProjectManagerViewProps> = ({ data }) => {
  const summary = data?.summary;
  const projects = data?.projects || [];
  const sites = data?.sites;
  const managedTeam = data?.team_managed_by_project_manager;
  const managedMembers = managedTeam?.members || data?.team?.members || [];
  const managedCount = summary?.managed_team_members ?? summary?.team_members ?? managedMembers.length;
  const attendanceToday = data?.team_attendance_today;

  const roleDistributionEntries = (() => {
    const byRole = managedTeam?.by_role;
    if (!byRole) return [];
    if (Array.isArray(byRole)) {
      return byRole.map((item) => ({
        name: getRoleName(item),
        count: toNumber(item.count),
      }));
    }
    return Object.entries(byRole).map(([name, count]) => ({
      name,
      count: toNumber(count),
    }));
  })();

  const sitesByWorkflow = sites?.by_workflow || [];
  const sitesByStatus = sites?.by_status || [];

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard title="Total Projects" value={summary.total_projects} icon={FaProjectDiagram} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
          <StatCard title="Active Projects" value={summary.active_projects} icon={FaChartLine} iconBgColor="bg-green-100" iconColor="text-green-600" />
          <StatCard title="Total Sites" value={summary.total_sites} icon={FaMapMarkerAlt} iconBgColor="bg-purple-100" iconColor="text-purple-600" />
          <StatCard title="Managed Team" value={managedCount} icon={FaUsers} iconBgColor="bg-indigo-100" iconColor="text-indigo-600" />
        </div>
      )}

      {attendanceToday && (
        <SectionCard title="Team Attendance Today" subtitle={formatDate(attendanceToday.date)} icon={FaUserClock}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MiniStat label="Total Members" value={attendanceToday.total_members} color="gray" />
            <MiniStat label="Present" value={attendanceToday.present} color="green" />
            <MiniStat label="Absent" value={attendanceToday.absent} color="red" />
            <MiniStat label="Attendance Rate" value={`${attendanceToday.attendance_rate.toFixed(1)}%`} color="blue" />
          </div>
        </SectionCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {roleDistributionEntries.length > 0 && (
          <SectionCard title="Managed Team by Role" icon={FaUsers}>
            <div className="h-64">
              <DoughnutChart
                labels={roleDistributionEntries.map((item) => item.name)}
                data={roleDistributionEntries.map((item) => item.count)}
                colors={CHART_COLORS.mixed}
              />
            </div>
          </SectionCard>
        )}

        {sites && (
          <SectionCard title="Sites Overview" icon={FaMapMarkerAlt}>
            <div className="h-64">
              <BarChart
                labels={(sitesByWorkflow.length > 0 ? sitesByWorkflow : sitesByStatus).map((item) =>
                  formatWorkflowStatus(
                    (item as { survey_workflow_status?: string; status?: string }).survey_workflow_status ||
                      (item as { survey_workflow_status?: string; status?: string }).status ||
                      ''
                  )
                )}
                datasets={[
                  {
                    label: 'Sites',
                    data: (sitesByWorkflow.length > 0 ? sitesByWorkflow : sitesByStatus).map((item) => item.count),
                  },
                ]}
                showLegend={false}
              />
            </div>
          </SectionCard>
        )}
      </div>

      {(managedTeam || data?.team) && (
        <SectionCard title="Managed Team Members" subtitle={`${managedMembers.length} members`} icon={FaUserTie}>
          <DataTable
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'email', label: 'Email' },
              { key: 'role', label: 'Role' },
            ]}
            data={managedMembers}
            maxHeight="320px"
            emptyMessage="No managed team members found"
          />
        </SectionCard>
      )}

      {projects.length > 0 && (
        <SectionCard title="Projects" icon={FaProjectDiagram}>
          <DataTable
            columns={[
              { key: 'project_name', label: 'Project' },
              { key: 'status', label: 'Status', render: (item) => <StatusBadge status={item.status} /> },
              { key: 'total_sites', label: 'Sites', align: 'center' },
              { key: 'completed_sites', label: 'Completed', align: 'center' },
              {
                key: 'completion_rate',
                label: 'Rate',
                align: 'center',
                render: (item) => `${toNumber(item.completion_rate).toFixed(1)}%`,
              },
            ]}
            data={projects}
            maxHeight="320px"
            emptyMessage="No projects assigned"
          />
        </SectionCard>
      )}
    </div>
  );
};

// Team Coordinator Dashboard (Role 4)
interface TeamCoordinatorViewProps {
  data: TeamCoordinatorDashboard;
}

export const TeamCoordinatorView: React.FC<TeamCoordinatorViewProps> = ({ data }) => {
  const {
    summary,
    engineer_attendance,
    pending_work,
    today_work,
    engineer_today_activity,
    engineer_kms_yesterday,
    site_stage_counts_current_month,
    sites,
    team,
    team_attendance_today,
    billing,
    claims,
    rejections,
  } = data;
  const teamAttendanceToday = team?.attendance_today || team_attendance_today;
  const teamProjects = team?.projects;
  const usersInSameProjects = team?.users_in_same_projects?.total_users;
  const pendingSites = pending_work?.sites_pending;

  const workflowFunnelData = sites.workflow_funnel
    ? Object.entries(sites.workflow_funnel)
        .filter(([key]) => key !== 'validation_completed')
        .map(([key, value]) => ({
          label: formatWorkflowStatus(key),
          value: value as number,
        }))
    : [];

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <StatCard
          title="Total Sites"
          value={summary.total_sites}
          icon={FaMapMarkerAlt}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Sites Created"
          value={summary.sites_created}
          icon={FaCheckDouble}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          title="Pending SE"
          value={summary.pending_se_assignment}
          icon={FaHourglassHalf}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
        />
        <StatCard
          title="On Hold"
          value={summary.on_hold}
          icon={FaExclamationTriangle}
          iconBgColor="bg-orange-100"
          iconColor="text-orange-600"
        />
        <StatCard
          title="Completed"
          value={summary.completed}
          icon={FaCheckDouble}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          title="Completion Rate"
          value={`${summary.completion_rate.toFixed(1)}%`}
          icon={FaChartLine}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />
        <StatCard
          title="Cancelled"
          value={summary.cancelled}
          icon={FaExclamationTriangle}
          iconBgColor="bg-red-100"
          iconColor="text-red-600"
        />
      </div>

      {(pendingSites || today_work) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pendingSites && (
            <SectionCard title="Pending Work" icon={FaHourglassHalf}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <MiniStat label="Engineer Assigned" value={pendingSites.engineer_assigned} color="blue" />
                <MiniStat label="Field Work Started" value={pendingSites.field_work_started} color="yellow" />
                <MiniStat label="Data Uploaded" value={pendingSites.data_uploaded} color="green" />
              </div>
            </SectionCard>
          )}

          {today_work && (
            <SectionCard title="Today's Work" subtitle={formatDate(today_work.date)} icon={FaClipboardCheck}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <MiniStat label="Field Work Started" value={today_work.field_work_started_sites} color="blue" />
                <MiniStat label="Field Work Completed" value={today_work.field_work_completed_sites} color="green" />
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {engineer_attendance && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard
              title="Engineer Attendance (Today)"
              subtitle={formatDate(engineer_attendance.today.date)}
              icon={FaUserClock}
            >
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <MiniStat label="Total" value={engineer_attendance.today.total_engineers} color="gray" />
                <MiniStat label="Marked" value={engineer_attendance.today.marked} color="blue" />
                <MiniStat label="Not Marked" value={engineer_attendance.today.not_marked} color="gray" />
                <MiniStat label="Pending" value={engineer_attendance.today.pending_approval} color="yellow" />
                <MiniStat label="Approved" value={engineer_attendance.today.approved} color="green" />
              </div>

              <div className="mt-4">
                <DataTable
                  columns={[
                    { key: 'name', label: 'Engineer' },
                    { key: 'username', label: 'Username' },
                    { key: 'state', label: 'State', render: (item) => item.state?.state_name || '-' },
                    { key: 'district', label: 'District', render: (item) => item.district?.name || '-' },
                    {
                      key: 'approval_status',
                      label: 'Status',
                      render: (item) => <StatusBadge status={item.approval_status} />,
                    },
                    { key: 'check_in', label: 'In', render: (item) => formatTime(item.attendance?.check_in_time) },
                    { key: 'check_out', label: 'Out', render: (item) => formatTime(item.attendance?.check_out_time) },
                    { key: 'work_status', label: 'Work', render: (item) => item.attendance?.work_status || '-' },
                  ]}
                  data={engineer_attendance.today.engineers || []}
                  maxHeight="320px"
                  emptyMessage="No engineer attendance for today"
                />
              </div>
            </SectionCard>

            <SectionCard
              title="Engineer Attendance (Yesterday)"
              subtitle={formatDate(engineer_attendance.yesterday.date)}
              icon={FaUserClock}
            >
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <MiniStat label="Total" value={engineer_attendance.yesterday.total_engineers} color="gray" />
                <MiniStat label="Marked" value={engineer_attendance.yesterday.marked} color="blue" />
                <MiniStat label="Not Marked" value={engineer_attendance.yesterday.not_marked} color="gray" />
                <MiniStat label="Pending" value={engineer_attendance.yesterday.pending_approval} color="yellow" />
                <MiniStat label="Approved" value={engineer_attendance.yesterday.approved} color="green" />
              </div>

              <div className="mt-4">
                <DataTable
                  columns={[
                    { key: 'name', label: 'Engineer' },
                    { key: 'username', label: 'Username' },
                    { key: 'state', label: 'State', render: (item) => item.state?.state_name || '-' },
                    { key: 'district', label: 'District', render: (item) => item.district?.name || '-' },
                    {
                      key: 'approval_status',
                      label: 'Status',
                      render: (item) => <StatusBadge status={item.approval_status} />,
                    },
                    { key: 'check_in', label: 'In', render: (item) => formatTime(item.attendance?.check_in_time) },
                    { key: 'check_out', label: 'Out', render: (item) => formatTime(item.attendance?.check_out_time) },
                    { key: 'work_status', label: 'Work', render: (item) => item.attendance?.work_status || '-' },
                  ]}
                  data={engineer_attendance.yesterday.engineers || []}
                  maxHeight="320px"
                  emptyMessage="No engineer attendance for yesterday"
                />
              </div>
            </SectionCard>
          </div>

          <SectionCard
            title="Engineer Attendance (Last 30 Days)"
            subtitle={`${formatDate(engineer_attendance.last_30_days.start_date)} - ${formatDate(
              engineer_attendance.last_30_days.end_date
            )}`}
            icon={FaUserClock}
          >
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <MiniStat label="Engineers" value={engineer_attendance.last_30_days.total_engineers} color="gray" />
              <MiniStat label="Marked" value={engineer_attendance.last_30_days.marked_entries} color="blue" />
              <MiniStat label="Not Marked" value={engineer_attendance.last_30_days.not_marked_entries} color="gray" />
              <MiniStat label="Pending" value={engineer_attendance.last_30_days.pending_approval_entries} color="yellow" />
              <MiniStat label="Approved" value={engineer_attendance.last_30_days.approved_entries} color="green" />
            </div>

            <div className="mt-4">
              <DataTable
                columns={[
                  { key: 'name', label: 'Engineer' },
                  { key: 'username', label: 'Username' },
                  { key: 'state', label: 'State', render: (item) => item.state?.state_name || '-' },
                  { key: 'district', label: 'District', render: (item) => item.district?.name || '-' },
                  { key: 'marked_days', label: 'Marked', align: 'center' },
                  { key: 'pending_approval_days', label: 'Pending', align: 'center' },
                  { key: 'approved_days', label: 'Approved', align: 'center' },
                  {
                    key: 'last_marked_date',
                    label: 'Last Marked',
                    render: (item) => (item.last_marked_date ? formatDate(item.last_marked_date) : '-'),
                  },
                ]}
                data={engineer_attendance.last_30_days.engineers || []}
                maxHeight="320px"
                emptyMessage="No engineer attendance history available"
              />
            </div>
          </SectionCard>
        </div>
      )}

      {(engineer_today_activity || engineer_kms_yesterday || site_stage_counts_current_month) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {engineer_today_activity && (
            <SectionCard title="Engineer Activity (Today)" subtitle={formatDate(engineer_today_activity.date)} icon={FaMapMarkerAlt}>
              <DataTable
                columns={[
                  { key: 'name', label: 'Engineer' },
                  { key: 'username', label: 'Username' },
                  {
                    key: 'first_site_start',
                    label: 'First Site Start',
                    render: (item) =>
                      item.first_site_start
                        ? `${item.first_site_start.site_id} • ${formatTime(item.first_site_start.started_at)}`
                        : '-',
                  },
                  {
                    key: 'last_site_completed',
                    label: 'Last Site Completed',
                    render: (item) =>
                      item.last_site_completed
                        ? `${item.last_site_completed.site_id} • ${formatTime(item.last_site_completed.completed_at)}`
                        : '-',
                  },
                ]}
                data={engineer_today_activity.engineers || []}
                maxHeight="320px"
                emptyMessage="No engineer activity for today"
              />
            </SectionCard>
          )}

          {engineer_kms_yesterday && (
            <SectionCard title="Engineer KMs (Yesterday)" subtitle={formatDate(engineer_kms_yesterday.date)} icon={FaTruck}>
              <DataTable
                columns={[
                  { key: 'name', label: 'Engineer' },
                  { key: 'username', label: 'Username' },
                  {
                    key: 'total_kms',
                    label: 'Total KMs',
                    align: 'center',
                    render: (item) => toNumber(item.total_kms).toFixed(1),
                  },
                  { key: 'total_sites', label: 'Sites', align: 'center' },
                  { key: 'sites_with_claim_distance', label: 'Claim Dist.', align: 'center' },
                ]}
                data={engineer_kms_yesterday.engineers || []}
                maxHeight="320px"
                emptyMessage="No KMs data for yesterday"
              />
            </SectionCard>
          )}

          {site_stage_counts_current_month && (
            <SectionCard
              title="Site Stages (Current Month)"
              subtitle={`${site_stage_counts_current_month.month} | ${formatDateTime(
                site_stage_counts_current_month.start_at
              )} - ${formatDateTime(site_stage_counts_current_month.end_at)}`}
              icon={FaCog}
            >
              {site_stage_counts_current_month.by_workflow_status?.length ? (
                <div className="h-64">
                  <BarChart
                    labels={site_stage_counts_current_month.by_workflow_status.map((s) =>
                      formatWorkflowStatus(s.survey_workflow_status)
                    )}
                    datasets={[
                      {
                        label: 'Sites',
                        data: site_stage_counts_current_month.by_workflow_status.map((s) => s.count),
                      },
                    ]}
                    showLegend={false}
                  />
                </div>
              ) : (
                <div className="text-sm text-gray-500">No site stage data available for this month.</div>
              )}
            </SectionCard>
          )}
        </div>
      )}

      {/* Team Attendance Today */}
      {teamAttendanceToday && (
        <SectionCard title="Team Attendance Today" subtitle={formatDate(teamAttendanceToday.date)} icon={FaUserClock}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{teamAttendanceToday.total_members}</p>
              <p className="text-sm text-gray-500">Total Members</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-700">{teamAttendanceToday.present}</p>
              <p className="text-sm text-green-600">Present</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-3xl font-bold text-red-700">{teamAttendanceToday.absent}</p>
              <p className="text-sm text-red-600">Absent</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-700">{teamAttendanceToday.attendance_rate.toFixed(1)}%</p>
              <p className="text-sm text-blue-600">Attendance Rate</p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Sites by Workflow */}
        <SectionCard title="Sites by Workflow" icon={FaCog}>
          <div className="h-64">
            <DoughnutChart
              labels={sites.by_workflow.map((s) => formatWorkflowStatus(s.survey_workflow_status))}
              data={sites.by_workflow.map((s) => s.count)}
              colors={CHART_COLORS.mixed}
            />
          </div>
        </SectionCard>

        {/* Sites by Priority */}
        <SectionCard title="Sites by Priority" icon={FaExclamationTriangle}>
          <div className="h-64">
            <BarChart
              labels={sites.by_priority.map((p) => p.site_priority)}
              datasets={[
                {
                  label: 'Sites',
                  data: sites.by_priority.map((p) => p.count),
                },
              ]}
              showLegend={false}
            />
          </div>
        </SectionCard>

        {/* Workflow Funnel */}
        <SectionCard title="Workflow Funnel" icon={FaChartLine}>
          <WorkflowFunnelChart data={workflowFunnelData.slice(0, 8)} />
        </SectionCard>
      </div>

      {(typeof usersInSameProjects === 'number' || teamProjects) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {typeof usersInSameProjects === 'number' && (
            <StatCard
              title="Team Coverage"
              value={usersInSameProjects}
              subtitle="Users in same projects"
              icon={FaUsers}
              iconBgColor="bg-blue-100"
              iconColor="text-blue-600"
            />
          )}

          {teamProjects && (
            <SectionCard title="Team Projects" icon={FaProjectDiagram}>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <MiniStat label="Total Assigned" value={teamProjects.total_assigned} color="blue" />
                  <MiniStat
                    label="Distinct Statuses"
                    value={Object.keys(teamProjects.by_status || {}).length}
                    color="green"
                  />
                </div>

                {Object.keys(teamProjects.by_status || {}).length > 0 && (
                  <div className="h-56">
                    <BarChart
                      labels={Object.keys(teamProjects.by_status)}
                      datasets={[
                        {
                          label: 'Projects',
                          data: Object.values(teamProjects.by_status || {}).map((value) => toNumber(value)),
                        },
                      ]}
                      showLegend={false}
                    />
                  </div>
                )}

                <DataTable
                  columns={[
                    { key: 'project_name', label: 'Project' },
                    { key: 'project_short_name', label: 'Code' },
                    { key: 'status', label: 'Status', render: (item) => <StatusBadge status={item.status} /> },
                  ]}
                  data={teamProjects.list || []}
                  maxHeight="220px"
                  emptyMessage="No team projects assigned"
                />
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* Team Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Site Engineers Performance */}
        {team?.site_engineers && (
          <SectionCard
            title="Site Engineers Performance"
            subtitle={`${team.site_engineers.count} engineers`}
            icon={FaUserTie}
          >
            <DataTable
              columns={[
                { key: 'name', label: 'Name' },
                { key: 'sites_assigned', label: 'Assigned', align: 'center' },
                { key: 'sites_completed', label: 'Completed', align: 'center' },
                {
                  key: 'completion_rate',
                  label: 'Rate',
                  align: 'center',
                  render: (item) => (
                    <span className={item.completion_rate >= 80 ? 'text-green-600' : 'text-yellow-600'}>
                      {item.completion_rate.toFixed(1)}%
                    </span>
                  ),
                },
              ]}
              data={team.site_engineers.performance || []}
              maxHeight="240px"
              emptyMessage="No site engineer performance available"
            />
          </SectionCard>
        )}

        {/* Technical Associates Performance */}
        {team?.technical_associates && (
          <SectionCard
            title="TA Performance"
            subtitle={`${team.technical_associates.count} TAs`}
            icon={FaClipboardCheck}
          >
            <DataTable
              columns={[
                { key: 'name', label: 'Name' },
                { key: 'pending_review', label: 'Pending', align: 'center' },
                { key: 'approved', label: 'Approved', align: 'center' },
                {
                  key: 'approval_rate',
                  label: 'Rate',
                  align: 'center',
                  render: (item) => (
                    <span className={item.approval_rate >= 80 ? 'text-green-600' : 'text-yellow-600'}>
                      {item.approval_rate.toFixed(1)}%
                    </span>
                  ),
                },
              ]}
              data={team.technical_associates.performance || []}
              maxHeight="240px"
              emptyMessage="No TA performance available"
            />
          </SectionCard>
        )}
      </div>

      {/* Rejections & Billing */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="TA Rejected"
          value={rejections.ta_rejected}
          icon={FaExclamationTriangle}
          iconBgColor="bg-red-100"
          iconColor="text-red-600"
        />
        <StatCard
          title="Report Rejected"
          value={rejections.report_rejected}
          icon={FaFileAlt}
          iconBgColor="bg-orange-100"
          iconColor="text-orange-600"
        />
        <StatCard
          title="Validation Rejected"
          value={rejections.validation_rejected}
          icon={FaClipboardCheck}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
        />
        <StatCard
          title="Total Billed"
          value={formatCurrency(billing.total_billed_amount)}
          icon={FaMoneyBillWave}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
      </div>

      {/* Recent Sites */}
      <SectionCard title="Recent Sites" icon={FaMapMarkerAlt}>
        <DataTable
          columns={[
            { key: 'site_id', label: 'Site ID' },
            { key: 'site_name', label: 'Name' },
            { key: 'workflow_status', label: 'Status', render: (item) => <StatusBadge status={item.workflow_status} /> },
            { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
            { key: 'site_engineer', label: 'SE' },
            { key: 'created_at', label: 'Created', render: (item) => formatDate(item.created_at) },
          ]}
          data={sites.recent}
          maxHeight="320px"
        />
      </SectionCard>
    </div>
  );
};

// Technical Associate Dashboard (Role 5)
// interface TechnicalAssociateViewProps {
//   data: TechnicalAssociateDashboard;
// }

// export const TechnicalAssociateView: React.FC<TechnicalAssociateViewProps> = ({ data }) => {
//   const { summary, pending_sites, quality_metrics } = data;

//   return (
//     <div className="space-y-6">
//       {/* Summary Stats */}
//       <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
//         <StatCard
//           title="Total Assigned"
//           value={summary.total_assigned}
//           icon={FaClipboardCheck}
//           iconBgColor="bg-blue-100"
//           iconColor="text-blue-600"
//         />
//         <StatCard
//           title="Pending Review"
//           value={summary.pending_review}
//           icon={FaHourglassHalf}
//           iconBgColor="bg-yellow-100"
//           iconColor="text-yellow-600"
//         />
//         <StatCard
//           title="Reviewed"
//           value={summary.reviewed_total}
//           icon={FaCheckDouble}
//           iconBgColor="bg-green-100"
//           iconColor="text-green-600"
//         />
//         <StatCard
//           title="Approved"
//           value={summary.approved}
//           icon={FaCheckDouble}
//           iconBgColor="bg-green-100"
//           iconColor="text-green-600"
//         />
//         <StatCard
//           title="Rejected"
//           value={summary.rejected}
//           icon={FaExclamationTriangle}
//           iconBgColor="bg-red-100"
//           iconColor="text-red-600"
//         />
//         <StatCard
//           title="This Month"
//           value={summary.reviewed_this_month}
//           icon={FaCalendarCheck}
//           iconBgColor="bg-purple-100"
//           iconColor="text-purple-600"
//         />
//         <StatCard
//           title="Approval Rate"
//           value={`${summary.approval_rate.toFixed(1)}%`}
//           icon={FaChartLine}
//           iconBgColor="bg-indigo-100"
//           iconColor="text-indigo-600"
//         />
//       </div>

//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
//         {/* Pending Sites */}
//         <SectionCard title="Pending Sites for Review" icon={FaHourglassHalf}>
//           <DataTable
//             columns={[
//               { key: 'site_id', label: 'Site ID' },
//               { key: 'site_name', label: 'Name' },
//               { key: 'engineer', label: 'Engineer' },
//               { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
//               { key: 'work_date', label: 'Work Date', render: (item) => formatDate(item.work_date) },
//             ]}
//             data={pending_sites}
//             maxHeight="320px"
//             emptyMessage="No pending sites for review"
//           />
//         </SectionCard>

//         {/* Quality Metrics */}
//         <SectionCard title="Quality Metrics" icon={FaChartLine}>
//           <div className="space-y-6">
//             <div className="flex items-center justify-center">
//               <ProgressRing
//                 value={quality_metrics.average_rating}
//                 maxValue={5}
//                 label="Average Rating"
//                 size="lg"
//                 color="#22c55e"
//               />
//             </div>
//             <div className="h-48">
//               <BarChart
//                 labels={quality_metrics.feedback_distribution.map((f) => f.ta_se_quality_feedback)}
//                 datasets={[
//                   {
//                     label: 'Feedback Count',
//                     data: quality_metrics.feedback_distribution.map((f) => f.count),
//                   },
//                 ]}
//                 showLegend={false}
//               />
//             </div>
//           </div>
//         </SectionCard>
//       </div>
//     </div>
//   );
// };

// Site Engineer Dashboard (Role 7)
interface SiteEngineerViewProps {
  data: SiteEngineerDashboard;
}

export const SiteEngineerView: React.FC<SiteEngineerViewProps> = ({ data }) => {
  const { sites, tickets, current_month_advances, attendance } = data;
  const att = attendance?.summary;

  const priorityData = sites?.priority_distribution;
  const priorityLabels = ['Critical', 'High', 'Medium', 'Low'];
  const priorityValues = [
    priorityData?.critical ?? 0,
    priorityData?.high ?? 0,
    priorityData?.medium ?? 0,
    priorityData?.low ?? 0,
  ];

  return (
    <div className="space-y-6">
      {/* Site Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <StatCard title="Total Assigned" value={sites?.summary?.total_assigned ?? 0} icon={FaMapMarkerAlt} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
        <StatCard title="Pending" value={sites?.summary?.pending ?? 0} icon={FaHourglassHalf} iconBgColor="bg-yellow-100" iconColor="text-yellow-600" />
        <StatCard title="In Progress" value={sites?.summary?.in_progress ?? 0} icon={FaCog} iconBgColor="bg-purple-100" iconColor="text-purple-600" />
        <StatCard title="Completed" value={sites?.summary?.completed ?? 0} icon={FaCheckDouble} iconBgColor="bg-green-100" iconColor="text-green-600" />
        <StatCard title="TA Rejected" value={sites?.summary?.ta_rejected ?? 0} icon={FaExclamationTriangle} iconBgColor="bg-red-100" iconColor="text-red-600" />
        <StatCard title="On Hold" value={sites?.summary?.on_hold ?? 0} icon={FaHourglassHalf} iconBgColor="bg-gray-100" iconColor="text-gray-600" />
        <StatCard title="Completion %" value={`${sites?.completion_rate ?? 0}%`} icon={FaChartLine} iconBgColor="bg-indigo-100" iconColor="text-indigo-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Priority Distribution */}
        <SectionCard title="Site Priority Distribution" icon={FaChartLine}>
          <div className="h-52">
            <BarChart
              labels={priorityLabels}
              datasets={[{ label: 'Sites', data: priorityValues }]}
              showLegend={false}
            />
          </div>
        </SectionCard>

        {/* Tickets */}
        {tickets && (
          <SectionCard title="My Tickets" icon={FaTools}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <MiniStat label="Total Raised" value={tickets.total_raised} />
              <MiniStat label="Open" value={tickets.open} />
              <MiniStat label="In Progress" value={tickets.in_progress} />
              <MiniStat label="Resolved" value={tickets.resolved} />
              <MiniStat label="Closed" value={tickets.closed} />
            </div>
          </SectionCard>
        )}
      </div>

      {/* Current Month Advances */}
      {current_month_advances && (
        <SectionCard title={`Advances — ${current_month_advances.month}`} icon={FaMoneyBillWave}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="Requested" value={current_month_advances.requested} icon={FaMoneyBillWave} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
            <StatCard title="Approved" value={current_month_advances.approved} icon={FaCheckDouble} iconBgColor="bg-green-100" iconColor="text-green-600" />
            <StatCard title="Rejected" value={current_month_advances.rejected} icon={FaExclamationTriangle} iconBgColor="bg-red-100" iconColor="text-red-600" />
            <StatCard title="Transferred" value={current_month_advances.transferred} icon={FaReceipt} iconBgColor="bg-teal-100" iconColor="text-teal-600" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Total Requested Amount</p>
              <p className="text-lg font-semibold text-gray-800">{formatCurrency(current_month_advances.total_requested_amount)}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Total Approved Amount</p>
              <p className="text-lg font-semibold text-green-700">{formatCurrency(current_month_advances.total_approved_amount)}</p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Attendance Summary */}
      {att && (
        <SectionCard title="Attendance Summary (Period)" icon={FaCalendarCheck}>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
            <MiniStat label="Present" value={att.present_days} />
            <MiniStat label="Absent" value={att.absent_days} />
            <MiniStat label="Late" value={att.late_days} />
            <MiniStat label="Leave" value={att.leave_days} />
            <MiniStat label="Worked Hrs" value={att.total_worked_hours ?? 0} />
            <MiniStat label="Attendance %" value={`${att.attendance_percentage}%`} />
          </div>
        </SectionCard>
      )}
    </div>
  );
};

// MIS Dashboard (Role 6)
// interface MISViewProps {
//   data: MISDashboard;
// }

// export const MISView: React.FC<MISViewProps> = ({ data }) => {
//   const { summary, unassigned_lists, assigned_pending_lists, billing } = data;

//   const rmAssignPending = unassigned_lists?.report_maker_assign_pending ?? data.pending_assignment ?? [];
//   const validatorAssignPending = unassigned_lists?.validator_assign_pending ?? [];
//   const sentToClientPending = unassigned_lists?.sent_to_client_pending ?? [];
//   const rmWise = assigned_pending_lists?.report_maker_wise ?? (data.report_makers ?? []).map((r: any) => ({ report_designer_username: r.report_designer_username, count: r.total_sites }));
//   const validatorWise = assigned_pending_lists?.validator_wise ?? [];

//   return (
//     <div className="space-y-6">
//       {/* Summary Stats — Stage pipeline */}
//       <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
//         <StatCard title="Total Assigned" value={summary.total_assigned} icon={FaClipboardCheck} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
//         <StatCard title="Pending RM Assign" value={summary.pending_rm_assignment} icon={FaHourglassHalf} iconBgColor="bg-yellow-100" iconColor="text-yellow-600" />
//         <StatCard title="RM Assigned" value={summary.report_maker_assigned} icon={FaUserTie} iconBgColor="bg-orange-100" iconColor="text-orange-600" />
//         <StatCard title="Report In Progress" value={summary.report_in_progress} icon={FaFileAlt} iconBgColor="bg-purple-100" iconColor="text-purple-600" />
//         <StatCard title="Report Completed" value={summary.report_completed ?? summary.reports_completed ?? 0} icon={FaCheckDouble} iconBgColor="bg-green-100" iconColor="text-green-600" />
//         <StatCard title="This Month Done" value={summary.current_month_done ?? 0} icon={FaCalendarCheck} iconBgColor="bg-teal-100" iconColor="text-teal-600" />
//       </div>

//       <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
//         <StatCard title="Validator Assigned" value={summary.validator_assigned ?? 0} icon={FaUserTie} iconBgColor="bg-indigo-100" iconColor="text-indigo-600" />
//         <StatCard title="Validation In Progress" value={summary.validation_in_progress ?? 0} icon={FaCog} iconBgColor="bg-violet-100" iconColor="text-violet-600" />
//         <StatCard title="Report Finalized" value={summary.report_finalized ?? 0} icon={FaCheckDouble} iconBgColor="bg-emerald-100" iconColor="text-emerald-600" />
//         <StatCard title="Pending Overall" value={summary.pending_overall ?? 0} icon={FaHourglassHalf} iconBgColor="bg-red-100" iconColor="text-red-600" />
//       </div>

//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
//         {/* Pending RM Assignment */}
//         <SectionCard title="Pending RM Assignment" icon={FaHourglassHalf}>
//           <DataTable
//             columns={[
//               { key: 'site_id', label: 'Site ID' },
//               { key: 'site_name', label: 'Name' },
//               { key: 'activity', label: 'Activity', render: (item) => (item as any).activity || '—' },
//               { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
//               { key: 'ta_work_date', label: 'TA Date', render: (item) => formatDate(item.ta_work_date) },
//             ]}
//             data={rmAssignPending}
//             maxHeight="280px"
//             emptyMessage="No pending RM assignments"
//           />
//         </SectionCard>

//         {/* Pending Validator Assignment */}
//         <SectionCard title="Pending Validator Assignment" icon={FaUserTie}>
//           <DataTable
//             columns={[
//               { key: 'site_id', label: 'Site ID' },
//               { key: 'site_name', label: 'Name' },
//               { key: 'report_maker', label: 'Report Maker' },
//               { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
//             ]}
//             data={validatorAssignPending}
//             maxHeight="280px"
//             emptyMessage="No sites pending validator"
//           />
//         </SectionCard>

//         {/* Pending Send To Client */}
//         <SectionCard title="Pending Send To Client" icon={FaCheckDouble}>
//           <DataTable
//             columns={[
//               { key: 'site_id', label: 'Site ID' },
//               { key: 'site_name', label: 'Name' },
//               { key: 'validator', label: 'Validator' },
//               { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
//             ]}
//             data={sentToClientPending}
//             maxHeight="280px"
//             emptyMessage="No finalized sites pending dispatch"
//           />
//         </SectionCard>
//       </div>

//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
//         {/* Report Makers Workload */}
//         <SectionCard title="Report Makers — Active Workload" icon={FaUserTie}>
//           {rmWise.length > 0 ? (
//             <div className="h-56">
//               <BarChart
//                 labels={rmWise.map((r: any) => r.report_designer_username || r.report_designer_username || '—')}
//                 datasets={[{ label: 'Sites Active', data: rmWise.map((r: any) => Number(r.count ?? r.total_sites ?? 0)) }]}
//                 showLegend={false}
//               />
//             </div>
//           ) : (
//             <p className="py-8 text-center text-sm text-gray-400">No active report maker workload</p>
//           )}
//         </SectionCard>

//         {/* Validators Workload */}
//         <SectionCard title="Validators — Active Workload" icon={FaUserTie}>
//           {validatorWise.length > 0 ? (
//             <div className="h-56">
//               <BarChart
//                 labels={validatorWise.map((v: any) => v.validator_username || '—')}
//                 datasets={[{ label: 'Sites Active', data: validatorWise.map((v: any) => Number(v.count ?? 0)) }]}
//                 showLegend={false}
//               />
//             </div>
//           ) : (
//             <p className="py-8 text-center text-sm text-gray-400">No active validator workload</p>
//           )}
//         </SectionCard>
//       </div>

//       {/* Billing status */}
//       {billing?.by_status?.length > 0 && (
//         <SectionCard title="Billing Status" icon={FaFileInvoice}>
//           <DataTable
//             columns={[
//               { key: 'billing_status', label: 'Status', render: (item) => <StatusBadge status={item.billing_status} /> },
//               { key: 'count', label: 'Count' },
//             ]}
//             data={billing.by_status}
//             maxHeight="200px"
//             emptyMessage="No billing data"
//           />
//         </SectionCard>
//       )}
//     </div>
//   );
// };

// Report Maker Dashboard (Role 8 & 9)
interface ReportMakerViewProps {
  data: ReportMakerDashboard;
}

export const ReportMakerView: React.FC<ReportMakerViewProps> = ({ data }) => {
  const { summary, pending_work, quality, pending_activity_wise, pending_project_wise, tickets } = data;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <StatCard
          title="Total Assigned"
          value={summary.total_assigned}
          icon={FaFileAlt}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Pending Start"
          value={summary.pending_to_start}
          icon={FaHourglassHalf}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
        />
        <StatCard
          title="In Progress"
          value={summary.in_progress}
          icon={FaCog}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />
        <StatCard
          title="Completed"
          value={summary.completed}
          icon={FaCheckDouble}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          title="Rejected"
          value={summary.rejected}
          icon={FaExclamationTriangle}
          iconBgColor="bg-red-100"
          iconColor="text-red-600"
        />
        <StatCard
          title="This Month"
          value={summary.completed_this_month}
          icon={FaCalendarCheck}
          iconBgColor="bg-indigo-100"
          iconColor="text-indigo-600"
        />
        <StatCard
          title="Reports Created"
          value={summary.reports_created}
          icon={FaFileAlt}
          iconBgColor="bg-teal-100"
          iconColor="text-teal-600"
        />
        <StatCard
          title="Completion Rate"
          value={`${summary.completion_rate.toFixed(1)}%`}
          icon={FaChartLine}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Work */}
        <SectionCard title="Pending Work" icon={FaHourglassHalf}>
          <DataTable
            columns={[
              { key: 'site_id', label: 'Site ID' },
              { key: 'site_name', label: 'Name' },
              { key: 'status', label: 'Status', render: (item) => <StatusBadge status={item.status} /> },
              { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
              { key: 'assigned_date', label: 'Assigned', render: (item) => formatDate(item.assigned_date) },
            ]}
            data={pending_work}
            maxHeight="320px"
            emptyMessage="No pending work"
          />
        </SectionCard>

        {/* Quality Feedback */}
        <SectionCard title="Quality Feedback" icon={FaChartLine}>
          <div className="h-64">
            <BarChart
              labels={quality.feedback_distribution.map((f) => f.report_designer_quality_feedback)}
              datasets={[
                {
                  label: 'Feedback Count',
                  data: quality.feedback_distribution.map((f) => f.count),
                },
              ]}
              showLegend={false}
            />
          </div>
        </SectionCard>
      </div>

      {/* Pending breakdown by activity and project */}
      {((pending_activity_wise && pending_activity_wise.length > 0) || (pending_project_wise && pending_project_wise.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pending_activity_wise && pending_activity_wise.length > 0 && (
            <SectionCard title="Pending — Activity Wise" icon={FaProjectDiagram}>
              <div className="h-52">
                <BarChart
                  labels={pending_activity_wise.map((a) => (a as any)['activity.activity_name'] || `Activity ${a.activity_id}`)}
                  datasets={[{ label: 'Pending Sites', data: pending_activity_wise.map((a) => Number(a.count)) }]}
                  showLegend={false}
                />
              </div>
            </SectionCard>
          )}
          {pending_project_wise && pending_project_wise.length > 0 && (
            <SectionCard title="Pending — Project Wise" icon={FaBuilding}>
              <div className="h-52">
                <BarChart
                  labels={pending_project_wise.map((p) => (p as any)['project.project_short_name'] || (p as any)['project.project_name'] || `Project ${p.project_id}`)}
                  datasets={[{ label: 'Pending Sites', data: pending_project_wise.map((p) => Number(p.count)) }]}
                  showLegend={false}
                />
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* Tickets */}
      {tickets && (
        <SectionCard title="My Tickets" icon={FaTools}>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <MiniStat label="Total Raised" value={tickets.total_raised} />
            <MiniStat label="Open" value={tickets.open} />
            <MiniStat label="In Progress" value={tickets.in_progress} />
            <MiniStat label="Resolved" value={tickets.resolved} />
            <MiniStat label="Closed" value={tickets.closed} />
          </div>
        </SectionCard>
      )}
    </div>
  );
};

// Report Validator Dashboard (Role 10)
interface ReportValidatorViewProps {
  data: ReportValidatorDashboard;
}

export const ReportValidatorView: React.FC<ReportValidatorViewProps> = ({ data }) => {
  const { summary, pending_validation, quality_metrics, pending_activity_wise, pending_project_wise, tickets } = data;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <StatCard
          title="Total Assigned"
          value={summary.total_assigned}
          icon={FaClipboardCheck}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Pending Start"
          value={summary.pending_to_start}
          icon={FaHourglassHalf}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
        />
        <StatCard
          title="In Progress"
          value={summary.in_progress}
          icon={FaCog}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />
        <StatCard
          title="Validated"
          value={summary.validated}
          icon={FaCheckDouble}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          title="Rejected"
          value={summary.rejected}
          icon={FaExclamationTriangle}
          iconBgColor="bg-red-100"
          iconColor="text-red-600"
        />
        <StatCard
          title="This Month"
          value={summary.validated_this_month}
          icon={FaCalendarCheck}
          iconBgColor="bg-indigo-100"
          iconColor="text-indigo-600"
        />
        <StatCard
          title="Reports Validated"
          value={summary.reports_validated}
          icon={FaFileAlt}
          iconBgColor="bg-teal-100"
          iconColor="text-teal-600"
        />
        <StatCard
          title="Approval Rate"
          value={`${summary.approval_rate.toFixed(1)}%`}
          icon={FaChartLine}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Validation */}
        <SectionCard title="Pending Validation" icon={FaHourglassHalf}>
          <DataTable
            columns={[
              { key: 'site_id', label: 'Site ID' },
              { key: 'site_name', label: 'Name' },
              { key: 'report_maker', label: 'Report Maker' },
              { key: 'priority', label: 'Priority', render: (item) => <StatusBadge status={item.priority} /> },
              { key: 'report_date', label: 'Report Date', render: (item) => formatDate(item.report_date) },
            ]}
            data={pending_validation}
            maxHeight="320px"
            emptyMessage="No pending validation"
          />
        </SectionCard>

        {/* Quality Metrics */}
        <SectionCard title="Quality Metrics" icon={FaChartLine}>
          <div className="space-y-6">
            <div className="flex items-center justify-center">
              <ProgressRing
                value={quality_metrics.average_rating}
                maxValue={5}
                label="Average Rating"
                size="lg"
                color="#22c55e"
              />
            </div>
            <div className="h-48">
              <BarChart
                labels={quality_metrics.feedback_distribution.map((f) => f.validator_report_designer_qa_feedback)}
                datasets={[
                  {
                    label: 'Feedback Count',
                    data: quality_metrics.feedback_distribution.map((f) => f.count),
                  },
                ]}
                showLegend={false}
              />
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Pending breakdown by activity and project */}
      {((pending_activity_wise && pending_activity_wise.length > 0) || (pending_project_wise && pending_project_wise.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pending_activity_wise && pending_activity_wise.length > 0 && (
            <SectionCard title="Pending — Activity Wise" icon={FaProjectDiagram}>
              <div className="h-52">
                <BarChart
                  labels={pending_activity_wise.map((a) => (a as any)['activity.activity_name'] || `Activity ${a.activity_id}`)}
                  datasets={[{ label: 'Pending Sites', data: pending_activity_wise.map((a) => Number(a.count)) }]}
                  showLegend={false}
                />
              </div>
            </SectionCard>
          )}
          {pending_project_wise && pending_project_wise.length > 0 && (
            <SectionCard title="Pending — Project Wise" icon={FaBuilding}>
              <div className="h-52">
                <BarChart
                  labels={pending_project_wise.map((p) => (p as any)['project.project_short_name'] || (p as any)['project.project_name'] || `Project ${p.project_id}`)}
                  datasets={[{ label: 'Pending Sites', data: pending_project_wise.map((p) => Number(p.count)) }]}
                  showLegend={false}
                />
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* Tickets */}
      {tickets && (
        <SectionCard title="My Tickets" icon={FaTools}>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <MiniStat label="Total Raised" value={tickets.total_raised} />
            <MiniStat label="Open" value={tickets.open} />
            <MiniStat label="In Progress" value={tickets.in_progress} />
            <MiniStat label="Resolved" value={tickets.resolved} />
            <MiniStat label="Closed" value={tickets.closed} />
          </div>
        </SectionCard>
      )}
    </div>
  );
};

// HR Dashboard (Role 17)
interface HRViewProps {
  data: HRDashboard;
}

export const HRView: React.FC<HRViewProps> = ({ data }) => {
  const { summary, employees, attendance_today, attendance_this_month, leaves, training } = data;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <StatCard
          title="Total Employees"
          value={summary.total_employees}
          subtitle={`${summary.active_employees} active`}
          icon={FaUsers}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          title="New This Month"
          value={summary.new_this_month}
          icon={FaUserTie}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          title="Pending Verification"
          value={summary.pending_verification}
          icon={FaHourglassHalf}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
        />
        <StatCard
          title="Pending Leaves"
          value={summary.pending_leaves}
          icon={FaCalendarCheck}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />
        <StatCard
          title="Pending Training"
          value={summary.pending_training}
          icon={FaGraduationCap}
          iconBgColor="bg-indigo-100"
          iconColor="text-indigo-600"
        />
      </div>

      {/* Today's Attendance */}
      <SectionCard title="Today's Attendance" subtitle={formatDate(attendance_today.date)} icon={FaUserClock}>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xl font-bold text-gray-900">{attendance_today.total_expected}</p>
            <p className="text-xs text-gray-500">Expected</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-xl font-bold text-green-700">{attendance_today.present}</p>
            <p className="text-xs text-green-600">Present</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-xl font-bold text-red-700">{attendance_today.absent}</p>
            <p className="text-xs text-red-600">Absent</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-xl font-bold text-purple-700">{attendance_today.on_leave}</p>
            <p className="text-xs text-purple-600">On Leave</p>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <p className="text-xl font-bold text-yellow-700">{attendance_today.idle}</p>
            <p className="text-xs text-yellow-600">Idle</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-xl font-bold text-blue-700">{attendance_today.worked}</p>
            <p className="text-xs text-blue-600">Worked</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xl font-bold text-gray-700">{attendance_today.not_marked}</p>
            <p className="text-xs text-gray-500">Not Marked</p>
          </div>
          <div className="text-center p-3 bg-indigo-50 rounded-lg">
            <p className="text-xl font-bold text-indigo-700">{attendance_today.attendance_rate.toFixed(1)}%</p>
            <p className="text-xs text-indigo-600">Rate</p>
          </div>
        </div>
      </SectionCard>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* Employees by Role */}
        <SectionCard title="Employees by Role" icon={FaUsers}>
          <div className="h-64">
            <DoughnutChart
              labels={employees.by_role.map((r) => getRoleName(r))}
              data={employees.by_role.map((r) => toNumber(r.count))}
              colors={CHART_COLORS.mixed}
            />
          </div>
        </SectionCard>

        {/* Employees by Status */}
        <SectionCard title="Employees by Status" icon={FaCog}>
          <div className="h-64">
            <BarChart
              labels={employees.by_status.map((s) => formatWorkflowStatus(s.status))}
              datasets={[
                {
                  label: 'Count',
                  data: employees.by_status.map((s) => s.count),
                },
              ]}
              showLegend={false}
            />
          </div>
        </SectionCard>

        {/* Leaves by Type */}
        <SectionCard title="Leaves by Type" icon={FaCalendarCheck}>
          <div className="h-64">
            <DoughnutChart
              labels={leaves.by_type.map((l) => getAttendanceLeaveTypeLabel(l.leave_type))}
              data={leaves.by_type.map((l) => l.count)}
              colors={CHART_COLORS.mixed}
            />
          </div>
        </SectionCard>
      </div>

      {/* Pending Leaves & Training */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Pending Leave Applications" icon={FaCalendarCheck}>
          <DataTable
            columns={[
              { key: 'employee', label: 'Employee' },
              { key: 'leave_type', label: 'Type', render: (item) => getAttendanceLeaveTypeLabel(item.leave_type) },
              { key: 'start_date', label: 'From', render: (item) => formatDate(item.start_date) },
              { key: 'end_date', label: 'To', render: (item) => formatDate(item.end_date) },
              { key: 'total_days', label: 'Days', align: 'center' },
            ]}
            data={leaves.pending_applications}
            maxHeight="280px"
            emptyMessage="No pending leave applications"
          />
        </SectionCard>

        <SectionCard title="Pending Training Requests" icon={FaGraduationCap}>
          <DataTable
            columns={[
              { key: 'candidate_name', label: 'Candidate' },
              { key: 'designation', label: 'Designation' },
              { key: 'location', label: 'Location' },
              { key: 'association_type', label: 'Type' },
              { key: 'created_at', label: 'Requested', render: (item) => formatDate(item.created_at) },
            ]}
            data={training.pending_requests}
            maxHeight="280px"
            emptyMessage="No pending training requests"
          />
        </SectionCard>
      </div>
    </div>
  );
};

// Billing Manager Dashboard (Role 16)
interface BillingManagerViewProps {
  data: BillingManagerDashboard;
}

export const BillingManagerView: React.FC<BillingManagerViewProps> = ({ data }) => {
  const { summary, billing_status, pending_wcc_list, pending_invoice_list, recent_billings, monthly_trends } = data;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Billings"
          value={summary.total_billings}
          icon={FaFileInvoice}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Pending Billing"
          value={summary.sites_pending_billing}
          icon={FaHourglassHalf}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
        />
        <StatCard
          title="Pending WCC"
          value={summary.pending_wcc}
          icon={FaClipboardCheck}
          iconBgColor="bg-orange-100"
          iconColor="text-orange-600"
        />
        <StatCard
          title="Invoices Approved"
          value={summary.invoices_approved}
          icon={FaCheckDouble}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          title="Total Invoiced"
          value={formatCurrency(summary.total_invoiced)}
          icon={FaMoneyBillWave}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
        <StatCard
          title="Collection Efficiency"
          value={`${summary.collection_efficiency.toFixed(1)}%`}
          icon={FaChartLine}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
          <p className="text-xs text-green-600 font-medium uppercase">Total Paid</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{formatCurrency(summary.total_paid)}</p>
        </div>
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg p-4 border border-yellow-100">
          <p className="text-xs text-yellow-600 font-medium uppercase">Outstanding</p>
          <p className="text-2xl font-bold text-yellow-700 mt-1">{formatCurrency(summary.outstanding)}</p>
        </div>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
          <p className="text-xs text-blue-600 font-medium uppercase">This Month</p>
          <p className="text-2xl font-bold text-blue-700 mt-1">{formatCurrency(summary.this_month_invoiced)}</p>
        </div>
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-100">
          <p className="text-xs text-purple-600 font-medium uppercase">This Year</p>
          <p className="text-2xl font-bold text-purple-700 mt-1">{formatCurrency(summary.this_year_invoiced)}</p>
        </div>
      </div>

      {/* Monthly Trends */}
      <SectionCard title="Monthly Billing Trends" icon={FaChartLine}>
        <div className="h-64">
          <LineChart
            labels={monthly_trends.map((t) => t.month_name)}
            datasets={[
              {
                label: 'Invoiced',
                data: monthly_trends.map((t) => t.invoiced),
                borderColor: CHART_COLORS.primary[1],
                backgroundColor: `${CHART_COLORS.primary[1]}20`,
              },
              {
                label: 'Paid',
                data: monthly_trends.map((t) => t.paid),
                borderColor: CHART_COLORS.success[1],
                backgroundColor: `${CHART_COLORS.success[1]}20`,
              },
            ]}
          />
        </div>
      </SectionCard>

      {/* Pending Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Pending WCC Approval" icon={FaHourglassHalf}>
          <DataTable
            columns={[
              { key: 'site_id', label: 'Site ID' },
              { key: 'site_name', label: 'Name' },
              { key: 'activity_amount', label: 'Amount', render: (item) => formatCurrency(item.activity_amount) },
              { key: 'created_at', label: 'Created', render: (item) => formatDate(item.created_at) },
            ]}
            data={pending_wcc_list}
            maxHeight="280px"
            emptyMessage="No pending WCC"
          />
        </SectionCard>

        <SectionCard title="Pending Invoices" icon={FaFileInvoice}>
          <DataTable
            columns={[
              { key: 'site_id', label: 'Site ID' },
              { key: 'site_name', label: 'Name' },
              { key: 'activity_amount', label: 'Amount', render: (item) => formatCurrency(item.activity_amount) },
              { key: 'wcc_date', label: 'WCC Date', render: (item) => formatDate(item.wcc_date) },
            ]}
            data={pending_invoice_list}
            maxHeight="280px"
            emptyMessage="No pending invoices"
          />
        </SectionCard>
      </div>

      {/* Recent Billings */}
      <SectionCard title="Recent Billings" icon={FaFileInvoice}>
        <DataTable
          columns={[
            { key: 'site_id', label: 'Site ID' },
            { key: 'site_name', label: 'Name' },
            { key: 'billing_status', label: 'Billing Status', render: (item) => <StatusBadge status={item.billing_status} /> },
            { key: 'invoice_number', label: 'Invoice #' },
            { key: 'invoice_amount', label: 'Amount', render: (item) => formatCurrency(item.invoice_amount) },
            { key: 'invoice_status', label: 'Invoice Status', render: (item) => <StatusBadge status={item.invoice_status} /> },
          ]}
          data={recent_billings}
          maxHeight="320px"
        />
      </SectionCard>
    </div>
  );
};

// Accounts Dashboard (Role 18)
interface AccountsViewProps {
  data: AccountsDashboard;
}

export const AccountsView: React.FC<AccountsViewProps> = ({ data }) => {
  const { summary, project_claims, general_claims, advances, cab_hiring, billing } = data;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          title="Pending Claims"
          value={summary.total_pending_claims}
          icon={FaReceipt}
          iconBgColor="bg-yellow-100"
          iconColor="text-yellow-600"
        />
        <StatCard
          title="Pending Advances"
          value={summary.total_pending_advances}
          icon={FaMoneyBillWave}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          title="Pending Cab"
          value={summary.total_pending_cab}
          icon={FaTruck}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
        />
        <StatCard
          title="Pending Invoices"
          value={summary.pending_invoice_payments}
          icon={FaFileInvoice}
          iconBgColor="bg-orange-100"
          iconColor="text-orange-600"
        />
        <StatCard
          title="Approved This Month"
          value={formatCurrency(summary.total_approved_this_month)}
          icon={FaCheckDouble}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
        />
      </div>

      {/* Claims Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SectionCard title="Project Claims" icon={FaReceipt} noPadding>
          <div className="p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Pending</span>
              <span className="font-medium text-yellow-600">{project_claims.pending}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Approved</span>
              <span className="font-medium text-green-600">{project_claims.approved}</span>
            </div>
            <div className="pt-2 border-t">
              <p className="text-xs text-gray-500">Total Approved</p>
              <p className="text-lg font-bold text-green-700">{formatCurrency(project_claims.total_approved_amount)}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="General Claims" icon={FaFileInvoice} noPadding>
          <div className="p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Pending</span>
              <span className="font-medium text-yellow-600">{general_claims.pending}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Approved</span>
              <span className="font-medium text-green-600">{general_claims.approved}</span>
            </div>
            <div className="pt-2 border-t">
              <p className="text-xs text-gray-500">Total Approved</p>
              <p className="text-lg font-bold text-green-700">{formatCurrency(general_claims.total_approved_amount)}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Advances" icon={FaMoneyBillWave} noPadding>
          <div className="p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Pending</span>
              <span className="font-medium text-yellow-600">{advances.pending}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Approved</span>
              <span className="font-medium text-green-600">{advances.approved}</span>
            </div>
            <div className="pt-2 border-t">
              <p className="text-xs text-gray-500">Total Approved</p>
              <p className="text-lg font-bold text-green-700">{formatCurrency(advances.total_approved_amount)}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Cab Hiring" icon={FaTruck} noPadding>
          <div className="p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Pending</span>
              <span className="font-medium text-yellow-600">{cab_hiring.pending}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Approved</span>
              <span className="font-medium text-green-600">{cab_hiring.approved}</span>
            </div>
            <div className="pt-2 border-t">
              <p className="text-xs text-gray-500">Total Amount</p>
              <p className="text-lg font-bold text-green-700">{formatCurrency(cab_hiring.total_amount)}</p>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Billing Summary */}
      <SectionCard title="Billing Summary" icon={FaFileInvoice}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 bg-green-50 rounded-lg">
            <p className="text-3xl font-bold text-green-700">{billing.paid}</p>
            <p className="text-sm text-green-600">Invoices Paid</p>
            <p className="text-lg font-semibold text-green-800 mt-2">{formatCurrency(billing.total_paid)}</p>
          </div>
          <div className="text-center p-6 bg-yellow-50 rounded-lg">
            <p className="text-3xl font-bold text-yellow-700">{billing.pending_payment}</p>
            <p className="text-sm text-yellow-600">Pending Payment</p>
          </div>
          <div className="h-48">
            <DoughnutChart
              labels={['Paid', 'Pending']}
              data={[billing.paid, billing.pending_payment]}
              colors={[CHART_COLORS.success[1], CHART_COLORS.warning[1]]}
              centerText={String(billing.paid + billing.pending_payment)}
              centerSubtext="Total"
            />
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

// Generic fallback for unsupported roles
interface GenericDashboardProps {
  roleName: string;
  data: any;
}

export const GenericDashboardView: React.FC<GenericDashboardProps> = ({ roleName, data }) => {
  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <FaTools className="w-12 h-12 mx-auto text-blue-400 mb-4" />
        <h3 className="text-lg font-semibold text-blue-900">{roleName} Dashboard</h3>
        <p className="text-sm text-blue-600 mt-2">
          A customized dashboard for this role is being developed.
        </p>
      </div>

      {/* Show any summary data if available */}
      {data?.summary && (
        <SectionCard title="Overview" icon={FaChartLine}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data.summary).map(([key, value]) => (
              <div key={key} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">
                  {typeof value === 'number' ? value.toLocaleString() : String(value)}
                </p>
                <p className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};
